import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Capacitor } from '@capacitor/core';
import { apiFetch, getToken, clearToken, isTokenExpired, track, API_BASE_URL, DPP_BASE_URL, formatDate } from '../dppApi';
import BrandAssistant from './BrandAssistant';
import ManageCatalogue from './ManageCatalogue';
import BrandMaintenance from './BrandMaintenance';
import BrandInquiries from './BrandInquiries';
import BrandBespoke from './BrandBespoke';
import BrandBequeathals from './BrandBequeathals';
import Pager from './Pager';
import AccountMenu from './AccountMenu';
import { usePaged } from '../hooks/usePaged';
import '../App.css';

const bloomMark = process.env.PUBLIC_URL + '/BloomFavicon.png';

const TRANSFER_TYPES = [
    { value: 'initial_sale', label: 'Initial Sale' },
    { value: 'resale', label: 'Resale' },
    { value: 'gift', label: 'Gift' },
    { value: 'inheritance', label: 'Inheritance' },
];

const PERIODS = [7, 30, 90];

// Monochrome greys for the top-products bars (darkest = most-viewed).
const BAR_GREYS = ['#111111', '#3a3a3a', '#6a6a6a', '#8f8f8f', '#A5A5A5'];

const pctText = (v) => (v == null ? '—' : `${v}%`);

// A single KPI tile. `delta` (nullable) renders a ▲/▼ chip beside the value.
const StatTile = ({ label, value, sub, delta }) => (
    <div className="insights-tile">
        <div className="insights-tile-label">{label}</div>
        <div className="insights-tile-value">
            {value}
            {delta != null && (
                <span className={`insights-delta${delta < 0 ? ' insights-delta--down' : ''}`}>
                    {delta >= 0 ? '▲' : '▼'} {Math.abs(delta)}%
                </span>
            )}
        </div>
        <div className="insights-tile-sub">{sub || ' '}</div>
    </div>
);

// Inline-SVG area line of daily passport views. Greyscale only.
const ViewsLineChart = ({ data }) => {
    if (!data || data.length === 0) {
        return <div className="insights-empty">No views yet in this period.</div>;
    }
    const W = 560, H = 150, P = 10;
    const max = Math.max(1, ...data.map(d => d.views));
    const n = data.length;
    const x = (i) => (n === 1 ? W / 2 : P + (i * (W - 2 * P)) / (n - 1));
    const y = (v) => H - P - (v / max) * (H - 2 * P);
    const pts = data.map((d, i) => [x(i), y(d.views)]);
    const line = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ');
    const area = `${line} L${x(n - 1).toFixed(1)} ${H - P} L${x(0).toFixed(1)} ${H - P} Z`;
    return (
        <svg className="insights-linechart" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label="Passport views over time">
            <path d={area} fill="#ECECEC" />
            <path d={line} fill="none" stroke="#111111" strokeWidth="2" vectorEffect="non-scaling-stroke" />
            {n === 1 && <circle cx={x(0)} cy={y(data[0].views)} r="3" fill="#111111" />}
        </svg>
    );
};

// Horizontal greyscale bars of the most-viewed products.
const TopProductsBars = ({ data }) => {
    if (!data || data.length === 0) {
        return <div className="insights-empty">No product views yet.</div>;
    }
    const max = Math.max(1, ...data.map(d => d.views));
    return (
        <div className="insights-bars">
            {data.map((d, i) => (
                <div className="insights-bar-row" key={d.product + i}>
                    <div className="insights-bar-label" title={d.product}>
                        {d.qr_code_id
                            ? <a className="insights-bar-link" href={`${DPP_BASE_URL}/jewellery/${d.qr_code_id}?as=brand`} target="_blank" rel="noreferrer">{d.product}</a>
                            : d.product}
                    </div>
                    <div className="insights-bar-track">
                        <div
                            className="insights-bar-fill"
                            style={{ width: `${(d.views / max) * 100}%`, background: BAR_GREYS[i] || '#A5A5A5' }}
                        />
                    </div>
                    <div className="insights-bar-value">{d.views}</div>
                </div>
            ))}
        </div>
    );
};

// Grey track + black fill completeness meter.
const Meter = ({ label, pct }) => (
    <div className="insights-meter">
        <div className="insights-meter-head">
            <span>{label}</span>
            <span>{pctText(pct)}</span>
        </div>
        <div className="insights-meter-track">
            <div className="insights-meter-fill" style={{ width: `${pct || 0}%` }} />
        </div>
    </div>
);

// In the packaged mobile app Ask Bloom sits directly under the brand header
// card, above the catalogue; on the web it stays in the sidebar beside them.
// Gated on the platform, not a breakpoint, so the web is untouched.
const ASSISTANT_FIRST = Capacitor.isNativePlatform();
// Packaged app only (like SCROLL_CATEGORIES in UserCollection) — not a
// breakpoint. The store menu spans the tab row on a phone so long addresses
// aren't clipped; a browser dragged narrow keeps the natural-width menu, which
// already fits because it is anchored to the row rather than to the icon.
const FILL_STORE_MENU = Capacitor.isNativePlatform();

// Icon-only store filter: a storefront button that opens a small menu of the brand's
// stores. Native <select> can't be icon-only, so this is a minimal custom menu. A dot
// on the icon marks that a specific store (not "All") is selected.
const StoreSwitcher = ({ stores, storeId, onPick }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return;
        const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [open]);
    const current = stores.find(s => s.store_id === storeId);
    // Heading = "City - Locality" (structured), falling back to the store's name.
    const primary = (s) => (s.city && s.locality) ? `${s.city} - ${s.locality}` : (s.name || s.city || 'Store');
    const label = storeId === 'all' || !current ? 'All stores' : primary(current);
    const choose = (id) => { onPick(id); setOpen(false); };
    return (
        <div className="store-switch" ref={ref}>
            <button type="button" className="store-btn" title={`Store: ${label}`} aria-label={`Store: ${label}`}
                    aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen(o => !o)}>
                {storeId !== 'all' && <span className="store-btn-dot" />}
                <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M4 9l1.2-4h13.6L20 9M4 9h16M4 9v10h16V9M4 9a2 2 0 0 0 4 0 2 2 0 0 0 4 0 2 2 0 0 0 4 0 2 2 0 0 0 4 0M10 19v-5h4v5" />
                </svg>
            </button>
            {open && (
                <div className={`store-menu${FILL_STORE_MENU ? ' store-menu--fill' : ''}`} role="listbox">
                    {/* Always offered, even for a single-store brand. It used to be
                        hidden below two stores, which left a brand whose remembered
                        pick was its only store with no way back to the unscoped view
                        — and anything with a NULL store_id (every product ingested
                        into an existing brand) silently absent from the dashboard. */}
                    <button type="button" className={`store-menu-row${storeId === 'all' ? ' is-on' : ''}`} onClick={() => choose('all')}>
                        <span className="store-menu-nm">All stores</span>
                        {storeId === 'all' && <span className="store-menu-dot" />}
                    </button>
                    {stores.length > 0 && <div className="store-menu-div" />}
                    {stores.map(s => (
                        <button key={s.store_id} type="button"
                                className={`store-menu-row${storeId === s.store_id ? ' is-on' : ''}`}
                                onClick={() => choose(s.store_id)}>
                            <span className="store-menu-text">
                                <span className="store-menu-nm">{primary(s)}</span>
                                {s.address && <span className="store-menu-sub">{s.address}</span>}
                            </span>
                            {storeId === s.store_id && <span className="store-menu-dot" />}
                        </button>
                    ))}
                    {stores.length === 0 && (
                        <div className="store-menu-row store-menu-empty">
                            <span className="store-menu-nm">No stores yet</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const BrandDashboard = () => {
    const navigate = useNavigate();
    const token = getToken('brand');
    const [products, setProducts] = useState([]);
    const [brand, setBrand] = useState(null);
    const [error, setError] = useState(null);
    const [transferPanel, setTransferPanel] = useState(null);
    const [certPanel, setCertPanel] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [analyticsDays, setAnalyticsDays] = useState(30);
    const [dashSection, setDashSection] = useState('catalogue');   // dashboard section tab
    const [catCollapsed, setCatCollapsed] = useState({ unsold: false, sold: false });   // collapse product sections
    // Store filter: scopes the maintenance/inquiries/bespoke queues to one store.
    // Remembered across logins ('all' = every store). "All stores" is always in the
    // menu, including for single-store brands, so a remembered pick can always be
    // cleared.
    const [stores, setStores] = useState([]);
    const [storeId, setStoreId] = useState(() => localStorage.getItem('bloom_brand_store') || 'all');
    const pickStore = (id) => { setStoreId(id); localStorage.setItem('bloom_brand_store', id); };

    const loadProducts = useCallback(async () => {
        const qs = storeId && storeId !== 'all' ? `?store_id=${storeId}` : '';
        try {
            setProducts(await apiFetch(`/api/products${qs}`, { token }));
        } catch (err) {
            setError(err.message);
        }
    }, [token, storeId]);

    const loadAnalytics = useCallback(async (days) => {
        try {
            setAnalytics(await apiFetch(`/api/brand/analytics/dashboard?days=${days}`, { token }));
        } catch {
            // Insights are non-critical; leave the rest of the dashboard usable.
            setAnalytics(null);
        }
    }, [token]);

    useEffect(() => {
        // An EXPIRED token is still a token — without the expiry check it slips past
        // here and every call below 401s, stranding the brand on a broken dashboard.
        if (!token || isTokenExpired(token)) { clearToken('brand'); navigate('/brand'); return; }
        apiFetch('/api/brand/me', { token }).then(setBrand).catch(() => {});
        apiFetch('/api/brand/stores', { token }).then(setStores).catch(() => {});
    }, [token, navigate]);

    // Catalogue (re)loads on mount and whenever the store filter changes — the
    // backend scopes by ?store_id=, so no client-side filtering is needed.
    useEffect(() => { if (token && !isTokenExpired(token)) loadProducts(); }, [loadProducts, token]);

    // Default selection: "All stores" when the brand has more than one store, else the
    // single (flagship) store. Also self-heals a stale remembered store_id (global
    // localStorage) from a different brand — "all" is only valid with >1 store.
    useEffect(() => {
        if (!stores.length) return;
        const defaultId = stores.length > 1 ? 'all' : stores[0].store_id;
        const valid = (storeId === 'all' && stores.length > 1) || stores.some(s => s.store_id === storeId);
        if (!valid) {
            setStoreId(defaultId);
            localStorage.setItem('bloom_brand_store', defaultId);
        }
    }, [stores, storeId]);

    useEffect(() => {
        if (token) loadAnalytics(analyticsDays);
    }, [token, analyticsDays, loadAnalytics]);

    // Reset the back stack so Android/browser Back after logout goes Home → exit,
    // not into now-tokenless screens: replace this page with Home, then show login.
    const logout = () => { clearToken('brand'); navigate('/', { replace: true }); navigate('/brand'); };

    const openCert = (productId, docType = 'certificate') =>
        setCertPanel({ productId, docType, file: null, issuedBy: '', issuedDate: '', busy: false, error: null, done: false });

    const closeCert = () => setCertPanel(null);

    const uploadDocument = async () => {
        if (!certPanel.file) return;
        setCertPanel(p => ({ ...p, busy: true, error: null }));
        try {
            const form = new FormData();
            form.append('document', certPanel.file);
            form.append('doc_type', certPanel.docType);
            if (certPanel.issuedBy) form.append('issued_by', certPanel.issuedBy);
            if (certPanel.issuedDate) form.append('issued_date', certPanel.issuedDate);
            await apiFetch(`/api/products/${certPanel.productId}/documents`, { method: 'POST', token, body: form, isForm: true });
            setCertPanel(p => ({ ...p, busy: false, done: true }));
            loadProducts();
        } catch (err) {
            setCertPanel(p => ({ ...p, busy: false, error: err.message }));
        }
    };

    // A piece has at most ONE open claim link (enforced by a partial unique index
    // server-side), and the product payload carries it as `open_transfer`. So the
    // panel opens onto whatever is already outstanding rather than a blank form —
    // re-opening it used to be the only way to find a link you'd lost, and
    // generating again silently superseded the one you'd already sent.
    const claimUrlFor = (tokenId) => `${window.location.origin}/transfer/claim/${tokenId}`;

    const openTransfer = (productId) =>
        setTransferPanel({ productId, type: 'initial_sale', mode: 'copy',
                           email: '', busy: false, error: null, copied: false });

    const closeTransfer = () => setTransferPanel(null);

    const generateLink = async () => {
        setTransferPanel(p => ({ ...p, busy: true, error: null }));
        try {
            await apiFetch(`/api/products/${transferPanel.productId}/sale-token`, {
                method: 'POST',
                token,
                body: { transfer_type: transferPanel.type },
            });
            await loadProducts();            // the new link arrives via open_transfer
            setTransferPanel(p => ({ ...p, busy: false, mode: 'copy' }));
        } catch (err) {
            setTransferPanel(p => ({ ...p, busy: false, error: err.message }));
        }
    };

    const sendLink = async (tokenId) => {
        setTransferPanel(p => ({ ...p, busy: true, error: null }));
        try {
            await apiFetch(`/api/transfer/send/${tokenId}`, {
                method: 'POST', token, body: { email: transferPanel.email },
            });
            await loadProducts();            // sent_to / sent_at come back on the token
            setTransferPanel(p => ({ ...p, busy: false, email: '' }));
        } catch (err) {
            setTransferPanel(p => ({ ...p, busy: false, error: err.message }));
        }
    };

    const cancelLink = async (tokenId) => {
        setTransferPanel(p => ({ ...p, busy: true, error: null }));
        try {
            await apiFetch(`/api/transfer/cancel/${tokenId}`, { method: 'POST', token });
            await loadProducts();
            setTransferPanel(p => ({ ...p, busy: false }));
        } catch (err) {
            setTransferPanel(p => ({ ...p, busy: false, error: err.message }));
        }
    };

    const unsold = products.filter(p => p.ownership_count === 0);
    const sold = products.filter(p => p.ownership_count > 0);

    // Each catalogue group pages independently — a brand can have hundreds of
    // products, and collapsing one shouldn't disturb the other's position.
    const unsoldPage = usePaged(unsold);
    const soldPage = usePaged(sold);

    const renderProductCard = (p, showTransfer) => (
        <div className="collection-card" key={p.product_id}>
            {p.image_url && p.image_url.startsWith('http')
                ? <img className="collection-card-img" src={p.image_url} alt={p.name} />
                : <div className="collection-card-img collection-card-img--empty">No image</div>}

            <div className="collection-card-body">
                <div className="collection-card-title">{p.name}</div>
                <div className="collection-card-meta">{p.metal_type} {p.metal_purity}</div>

                <div className="collection-card-footer">
                    <div className="collection-card-actions">
                    <a className="collection-action" href={`${DPP_BASE_URL}/jewellery/${p.qr_code_id}?as=brand`} target="_blank" rel="noreferrer">Open passport</a>
                    <button
                        className="collection-action"
                        onClick={() =>
                            certPanel?.productId === p.product_id && certPanel?.docType === 'certificate'
                                ? closeCert()
                                : openCert(p.product_id, 'certificate')
                        }
                    >
                        {certPanel?.productId === p.product_id && certPanel?.docType === 'certificate' ? 'Cancel' : 'Attach certificate'}
                    </button>
                    {!showTransfer && (
                        <button
                            className="collection-action"
                            onClick={() =>
                                certPanel?.productId === p.product_id && certPanel?.docType === 'receipt'
                                    ? closeCert()
                                    : openCert(p.product_id, 'receipt')
                            }
                        >
                            {certPanel?.productId === p.product_id && certPanel?.docType === 'receipt' ? 'Cancel' : 'Upload receipt'}
                        </button>
                    )}
                    {showTransfer && (
                        <button
                            className="collection-action"
                            onClick={() =>
                                transferPanel?.productId === p.product_id
                                    ? closeTransfer()
                                    : openTransfer(p.product_id)
                            }
                        >
                            {transferPanel?.productId === p.product_id
                                ? 'Close'
                                : (p.open_transfer ? 'View transfer' : 'Transfer')}
                        </button>
                    )}
                    </div>
                    <img className="collection-card-qr" src={`${API_BASE_URL}${p.qr_image_url}`} alt="QR code" />
                </div>

                {certPanel?.productId === p.product_id && (
                    <div className="dpp-transfer-panel">
                    {certPanel.done ? (
                        <p className="catalogue-hint catalogue-hint--done">
                            {certPanel.docType === 'receipt' ? 'Receipt uploaded.' : 'Certificate uploaded.'}
                        </p>
                    ) : (
                        <div className="catalogue-body">
                            <label className="dpp-transfer-label">
                                {certPanel.docType === 'receipt' ? 'Receipt file' : 'Certificate file'}
                            </label>
                            <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={e => setCertPanel(p => ({ ...p, file: e.target.files[0] || null }))}
                            />
                            <label className="dpp-transfer-label">
                                Issued by <span className="catalogue-nice">optional</span>
                            </label>
                            <input
                                className="dpp-input"
                                placeholder={certPanel.docType === 'receipt' ? 'e.g. Store name' : 'e.g. GIA'}
                                value={certPanel.issuedBy}
                                onChange={e => setCertPanel(p => ({ ...p, issuedBy: e.target.value }))}
                            />
                            <label className="dpp-transfer-label">
                                Issued date <span className="catalogue-nice">optional</span>
                            </label>
                            <input
                                className="dpp-input"
                                type="date"
                                value={certPanel.issuedDate}
                                onChange={e => setCertPanel(p => ({ ...p, issuedDate: e.target.value }))}
                            />
                            {certPanel.error && <p className="dpp-error">{certPanel.error}</p>}
                            <button
                                className="catalogue-btn-primary"
                                onClick={uploadDocument}
                                disabled={certPanel.busy || !certPanel.file}
                            >
                                {certPanel.busy ? 'Uploading…' : 'Upload'}
                            </button>
                        </div>
                    )}
                </div>
            )}

            {showTransfer && transferPanel?.productId === p.product_id && (
                <div className="dpp-transfer-panel">
                    {!p.open_transfer ? (
                        <div className="catalogue-body">
                            <label className="dpp-transfer-label">Transfer type</label>
                            <select
                                className="dpp-input"
                                value={transferPanel.type}
                                onChange={e => setTransferPanel(prev => ({ ...prev, type: e.target.value }))}
                            >
                                {TRANSFER_TYPES.map(({ value, label }) => (
                                    <option key={value} value={value}>{label}</option>
                                ))}
                            </select>
                            {transferPanel.error && <p className="dpp-error">{transferPanel.error}</p>}
                            <button className="catalogue-btn-primary" onClick={generateLink} disabled={transferPanel.busy}>
                                {transferPanel.busy ? 'Generating…' : 'Generate transfer link'}
                            </button>
                        </div>
                    ) : (
                        <div className="catalogue-body">
                            <p className="transfer-status">
                                <b>{TRANSFER_TYPES.find(t => t.value === p.open_transfer.transfer_type)?.label
                                    || p.open_transfer.transfer_type}</b>
                                {p.open_transfer.status === 'expired'
                                    ? ' · link expired'
                                    : ` · open · expires ${formatDate(p.open_transfer.expires_at)}`}
                            </p>

                            {p.open_transfer.status === 'expired' ? (
                                <button className="catalogue-btn-primary" onClick={generateLink} disabled={transferPanel.busy}>
                                    {transferPanel.busy ? 'Generating…' : 'Generate a new link'}
                                </button>
                            ) : (
                                <>
                                    <div className="transfer-modes">
                                        {['scan', 'email', 'copy'].map(m => (
                                            <button
                                                key={m}
                                                className={transferPanel.mode === m ? 'is-active' : ''}
                                                onClick={() => setTransferPanel(prev => ({ ...prev, mode: m, error: null }))}
                                            >
                                                {m === 'scan' ? 'Scan' : m === 'email' ? 'Email' : 'Copy'}
                                            </button>
                                        ))}
                                    </div>

                                    {transferPanel.mode === 'scan' && (
                                        <>
                                            <QRCodeSVG
                                                className="transfer-qr"
                                                value={claimUrlFor(p.open_transfer.token_id)}
                                                size={150}
                                                marginSize={2}
                                            />
                                            <p className="transfer-cap">Customer scans to claim</p>
                                        </>
                                    )}

                                    {transferPanel.mode === 'email' && (
                                        <>
                                            <input
                                                className="dpp-input"
                                                type="email"
                                                placeholder="customer@email.com"
                                                value={transferPanel.email}
                                                onChange={e => setTransferPanel(prev => ({ ...prev, email: e.target.value }))}
                                            />
                                            <button
                                                className="transfer-action"
                                                onClick={() => sendLink(p.open_transfer.token_id)}
                                                disabled={transferPanel.busy || !transferPanel.email}
                                            >
                                                {transferPanel.busy ? 'Sending…' : 'Send'}
                                            </button>
                                            {p.open_transfer.sent_to && (
                                                <p className="transfer-sent">
                                                    Sent to <b>{p.open_transfer.sent_to}</b><br />
                                                    {formatDate(p.open_transfer.sent_at)}
                                                </p>
                                            )}
                                        </>
                                    )}

                                    {transferPanel.mode === 'copy' && (
                                        <>
                                            <p className="transfer-link">{claimUrlFor(p.open_transfer.token_id)}</p>
                                            <button
                                                className="transfer-action"
                                                onClick={() => {
                                                    navigator.clipboard.writeText(claimUrlFor(p.open_transfer.token_id));
                                                    setTransferPanel(prev => ({ ...prev, copied: true }));
                                                }}
                                            >
                                                {transferPanel.copied ? 'Copied' : 'Copy'}
                                            </button>
                                            <p className="transfer-warn">
                                                Anyone with this link and a Bloom account can claim this piece.
                                            </p>
                                        </>
                                    )}

                                    {transferPanel.error && <p className="dpp-error">{transferPanel.error}</p>}
                                    <hr className="transfer-rule" />
                                    <button
                                        className="transfer-cancel"
                                        onClick={() => cancelLink(p.open_transfer.token_id)}
                                        disabled={transferPanel.busy}
                                    >
                                        Cancel this link
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                </div>
            )}
            </div>
        </div>
    );

    return (
        <div className="dpp-page">
            <div className="collection-topbar">
                <div className="collection-topbar-brand">
                    <img className="dpp-brand-mark" src={bloomMark} alt="Bloom" />
                    <h1 className="collection-topbar-title">Brand Dashboard</h1>
                </div>
                {/* Same account control as the collection: passing onSettings makes
                    the shared component render the identity header + "My Account"
                    pill + quiet Log out, identical to the user dropdown. The pill
                    lands on the brand account page. */}
                <AccountMenu
                    name={brand?.brand_title || brand?.name}
                    onLogout={logout}
                    onSettings={() => navigate('/brand/profile')}
                />
            </div>

            {error && <p className="dpp-error" style={{ maxWidth: 960, margin: '0 auto 16px' }}>{error}</p>}

            <div className="dpp-dashboard-cols">
                <div className="dpp-dashboard-main">
                    <div className="dpp-brand-profile-card dpp-card">
                        <div
                            className="dpp-brand-header-logo-box"
                            style={brand?.header_logo_bg_color
                                ? { backgroundColor: brand.header_logo_bg_color }
                                : undefined}
                        >
                            {brand?.header_logo_url &&
                                <img className="dpp-brand-header-logo-img" src={brand.header_logo_url} alt="Brand header logo" />
                            }
                        </div>
                    </div>

                    {ASSISTANT_FIRST && <BrandAssistant token={token} />}

                    <div className="dpp-products-box">
                        <div className="brand-tabrow">
                        <div className="catalogue-toggle collection-cat-menu">
                            {[
                                { key: 'catalogue', label: 'Catalogue' },
                                { key: 'editor', label: 'Editor' },
                                { key: 'maintenance', label: 'Maintenance' },
                                { key: 'inquiries', label: 'Inquiries' },
                                { key: 'transfers', label: 'Bequeathals' },
                                { key: 'bespoke', label: 'Bespoke' },
                            ].map(s => (
                                <button
                                    key={s.key}
                                    className={`catalogue-tab${dashSection === s.key ? ' is-active' : ''}`}
                                    onClick={() => { setDashSection(s.key); track('brand', `dashboard_section:${s.key}`); }}
                                >
                                    {s.label}
                                </button>
                            ))}
                        </div>
                        <StoreSwitcher stores={stores} storeId={storeId} onPick={pickStore} />
                        </div>

                        {dashSection === 'catalogue' && (
                            <>
                                <button type="button" className="inquiry-group-title inquiry-group-toggle" style={{ margin: '28px 0 14px' }}
                                    onClick={() => setCatCollapsed(c => ({ ...c, unsold: !c.unsold }))} aria-expanded={!catCollapsed.unsold}>
                                    <span className={`inquiry-group-chevron${catCollapsed.unsold ? '' : ' is-open'}`} aria-hidden="true">
                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 6 15 12 9 18" /></svg>
                                    </span>
                                    <span>Your products</span><span className="inquiry-group-count">{unsold.length}</span>
                                </button>
                                {!catCollapsed.unsold && (unsold.length === 0
                                    ? <p className="collection-status collection-status--flush section-empty">No unsold products.</p>
                                    : (
                                        <>
                                            <div className="collection-grid collection-grid--left">
                                                {unsoldPage.items.map(p => renderProductCard(p, true))}
                                            </div>
                                            <Pager
                                                page={unsoldPage.page}
                                                pageCount={unsoldPage.pageCount}
                                                onPage={unsoldPage.setPage}
                                                total={unsoldPage.total}
                                                pageSize={unsoldPage.pageSize}
                                                label="products"
                                            />
                                        </>
                                    ))}

                                {sold.length > 0 && (
                                    <>
                                        <button type="button" className="inquiry-group-title inquiry-group-toggle"
                                            style={{ margin: '28px 0 14px' }}
                                            onClick={() => setCatCollapsed(c => ({ ...c, sold: !c.sold }))} aria-expanded={!catCollapsed.sold}>
                                            <span className={`inquiry-group-chevron${catCollapsed.sold ? '' : ' is-open'}`} aria-hidden="true">
                                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 6 15 12 9 18" /></svg>
                                            </span>
                                            <span>Sold products</span><span className="inquiry-group-count">{sold.length}</span>
                                        </button>
                                        {!catCollapsed.sold && (
                                            <>
                                                <div className="collection-grid collection-grid--left">
                                                    {soldPage.items.map(p => renderProductCard(p, false))}
                                                </div>
                                                <Pager
                                                    page={soldPage.page}
                                                    pageCount={soldPage.pageCount}
                                                    onPage={soldPage.setPage}
                                                    total={soldPage.total}
                                                    pageSize={soldPage.pageSize}
                                                    label="products"
                                                />
                                            </>
                                        )}
                                    </>
                                )}
                            </>
                        )}

                        {dashSection === 'editor' && (
                            <ManageCatalogue token={token} onImported={loadProducts} defaultCurrency={brand?.default_currency} />
                        )}

                        {dashSection === 'maintenance' && <BrandMaintenance token={token} storeId={storeId} />}
                        {dashSection === 'inquiries' && <BrandInquiries token={token} storeId={storeId} />}
                        {dashSection === 'transfers' && <BrandBequeathals token={token} />}
                        {dashSection === 'bespoke' && <BrandBespoke token={token} storeId={storeId} />}
                    </div>
                </div>

                <aside className="dpp-dashboard-side">
                    {!ASSISTANT_FIRST && <BrandAssistant token={token} />}

                    {analytics && (
                        <div className="insights-section">
                            <div className="insights-header">
                                <h2 className="collection-section-title collection-section-title--flush" style={{ margin: 0 }}>Insights</h2>
                                <div className="insights-period">
                                    {PERIODS.map(d => (
                                        <button
                                            key={d}
                                            className={`insights-period-btn${analyticsDays === d ? ' is-active' : ''}`}
                                            onClick={() => setAnalyticsDays(d)}
                                        >
                                            {d}d
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="dpp-card insights-card">
                                <div className="insights-card-title">Passport views</div>
                                <div className="insights-stat">
                                    <span className="insights-stat-value">{analytics.kpis.views.value}</span>
                                    {analytics.kpis.views.delta_pct != null && (
                                        <span className={`insights-delta${analytics.kpis.views.delta_pct < 0 ? ' insights-delta--down' : ''}`}>
                                            {analytics.kpis.views.delta_pct >= 0 ? '▲' : '▼'} {Math.abs(analytics.kpis.views.delta_pct)}%
                                        </span>
                                    )}
                                    <span className="insights-stat-sub">last {analytics.days} days</span>
                                </div>
                                <ViewsLineChart data={analytics.views_over_time} />
                            </div>

                            <div className="insights-charts">
                                <div className="dpp-card insights-card">
                                    <div className="insights-card-title">Most viewed products</div>
                                    <TopProductsBars data={analytics.top_products} />
                                </div>
                            </div>

                            <div className="dpp-card insights-card">
                                <div className="insights-card-title">Catalogue Completeness</div>
                                <div className="insights-meters">
                                    <Meter label="Image" pct={analytics.catalog_completeness.image_pct} />
                                    <Meter label="Certificate" pct={analytics.catalog_completeness.certificate_pct} />
                                    <Meter label="Compliance" pct={analytics.catalog_completeness.compliance_pct} />
                                </div>
                            </div>

                            <div className="insights-tiles">
                                <StatTile
                                    label="Products with Passports"
                                    value={pctText(analytics.kpis.products_live.pct)}
                                    sub={`${analytics.kpis.products_live.live} of ${analytics.kpis.products_live.total}`}
                                />
                            </div>
                        </div>
                    )}
                </aside>
            </div>
        </div>
    );
};

export default BrandDashboard;
